<template>
  <ParentContractInfo
    v-if="contract.isParent"
    :contract="contract"
    @toggle-edit="$emit('toggle-edit')"
  />
  <v-container v-else>
    <v-row
      justify="end"
      align-content="end"
    >
      <v-spacer />
      <v-btn
        v-if="contract.parentContractId"
        rounded
        dense
        small
        color="primary"
        outlined
        class="mr-2"
        @click="$router.push({ name: 'contract', params: { contractId: contract.parentContractId }})"
      >
        {{ $t('contract.goToParent') }}
        <v-icon>mdi-open-in-new</v-icon>
      </v-btn>
      <v-btn
        rounded
        dense
        small
        color="primary"
        @click="$emit('toggle-edit')"
        :disabled="!canUserEdit || (contract.parts && contract.parts.length === 0)"
      >
        {{ $t('common.edit') }}
        <v-icon> mdi-pencil </v-icon>
      </v-btn>
    </v-row>
    <v-row>
      <v-col>
        <v-card>
          <v-card-text>
            <v-row>
              <v-col cols="6">
                <small><b> {{ $t('contract.name') }}</b> </small>
                <p>{{contract.contractName}}</p>
              </v-col>
              <v-col col="6">
                <small> <b>{{ $t('contract.priceBreakDown') }}</b> </small>
                <p><a
                    :href="contract.priceBreakdown"
                    target="_blank"
                  >{{ contract.priceBreakdown }}</a></p>
              </v-col>
              <v-col cols="3">
                <small> <b>{{ $tc('contract.area', 1) }}</b> </small>
                <p>{{contract.area ? contract.area.name : ''}}</p>
              </v-col>
              <v-col cols="3">
                <small> <b>{{ $tc(`contract.project.${contract.area ? contract.area.code : 'OFF'}`, 1) }}</b> </small>
                <p>{{projectDisplayName}}</p>
              </v-col>
              <v-col cols="3">
                <small> <b>{{ $tc('contract.package', 1) }}</b> </small>
                <p>{{contract.package}}</p>
              </v-col>
              <v-col cols="3">
                <small> <b>{{ $tc('contract.type', 1) }}</b> </small>
                <p>{{contract.contractType}}</p>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col>
        <v-card>
          <v-card-text>
            <v-row>
              <v-col>
                <small> <b>{{ $tc('contract.employer', 1) }}</b> </small>
                <p>{{employer}}<br /><i>{{employerAddress}}</i></p>
              </v-col>
              <v-col>
                <small> <b>{{ $tc('contract.contractor', 1) }}</b> </small>
                <p>{{contractor}}<br /><i>{{contractorAddress}}</i></p>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <v-card class="mt-5">
          <v-card-text>
            <v-row>
              <v-col>
                <small> <b>{{ $t('contract.contractManager') }}</b> </small>
                <p>{{contractManager}}</p>
              </v-col>
              <v-col>
                <small> <b>{{ $t('contract.packageManager') }}</b> </small>
                <p>{{packageManager}}</p>
              </v-col>
              <v-col>
                <small> <b>{{ $t('contract.projectController') }}</b> </small>
                <p>{{projectController}}</p>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

    </v-row>

    <v-row>
      <v-col cols="5">
        <v-card>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <small> <b>{{ $t('contract.signedPrice') }}</b> </small>
                <PricesDisplay
                  :prices="contract.signedPriceCentsPerCurrency"
                  :contract-currencies="contract.currencies"
                  :chosen-currency="contract.chosenCurrency"
                  :item-currency="contract.chosenCurrency"
                />
                <span>
                  <small> <b>{{ $t('contract.adjustedPrice') }}</b> </small>
                  <InfoTooltip
                    :text="$t('contract.adjustedPriceTip')"
                  />
                  <PricesDisplay
                    :prices="adjustedPriceCentsPerCurrency"
                    :contract-currencies="contract.currencies"
                    :chosen-currency="contract.chosenCurrency"
                    :item-currency="contract.chosenCurrency"
                    :percent-text="$t('contract.percentOfSignedPrice', { percent: adjustedPricePercent })"
                  />
                </span>

                <span>
                  <small> <b>{{ $t('contract.forecastedPrice') }}</b> </small>
                  <InfoTooltip
                    :text="$t('contract.forecastedPriceTip')"
                  />
                  <PricesDisplay
                    :prices="forecastedPriceCentsPerCurrency"
                    :contract-currencies="contract.currencies"
                    :chosen-currency="contract.chosenCurrency"
                    :item-currency="contract.chosenCurrency"
                    :percent-text="$t('contract.percentOfSignedPrice', { percent: forecastedPricePercent })"
                  />
                </span>

                <small> <b>{{ $t('contract.alreadyPaid') }}</b> </small>
                <PricesDisplay
                  :prices="currentPriceCentsPerCurrency"
                  :contract-currencies="contract.currencies"
                  :chosen-currency="contract.chosenCurrency"
                  :item-currency="contract.chosenCurrency"
                  :percent-text="`(${currentPercent}%)`"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="4">
        <v-card>
          <v-card-text>

            <small> <b>{{ $t('contract.afterNegotiations') }}</b> </small>
            <PricesDisplay
              :prices="afterNegotiationsPerCurrency"
              :contract-currencies="contract.currencies"
              :chosen-currency="contract.chosenCurrency"
              :item-currency="contract.chosenCurrency"
            />
            <small> <b>{{ $t('contract.beforeNegotiations') }}</b> </small>
            <PricesDisplay
              :prices="beforeNegotiationsPerCurrency"
              :contract-currencies="contract.currencies"
              :chosen-currency="contract.chosenCurrency"
              :item-currency="contract.chosenCurrency"
            />
            <small> <b>{{ $t('contract.savings') }}</b> </small>
            <p>{{ savings }}</p>

          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="3">
        <v-card>
          <v-card-text>
            <small> <b>{{ $tc('contract.currency', 2) }}</b> </small>
            <v-simple-table dense>
              <tbody>
                <tr
                  v-for="(currency, index) in contract.currencies"
                  :key="index"
                >
                  <td>{{currency.key}}</td>
                  <td>{{currency.value}}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12">
        <v-card>
          <v-card-text>
            <small> <b>{{ $tc(partTranslationKey + '.self', 2) }}</b> </small>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>{{ $t(partTranslationKey + '.name') }}</th>
                  <th>{{ $t('part.orderNumber') }}</th>
                  <th>{{ $t('part.ratio') }}</th>
                  <th>{{ $t(partTranslationKey + '.price') }}</th>
                  <th>{{ $t('part.forecastedPrice') }}</th>
                  <th>{{ $t('part.adjustedPrice') }}</th>
                  <th>{{ $t('part.currentPrice') }}</th>
                  <th>{{ $t('part.contractor') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(part, index) in parts"
                  :key="index"
                >
                  <td>{{part.name}}</td>
                  <td>{{part.purchaseOrder}}</td>
                  <td>{{part.ratio}}%</td>
                  <td>{{part.signedPriceCents | formatCurrency({ key: part.currency, value: 1 })}}</td>
                  <td>{{part.forecastedPriceCentsCombined | formatCurrency({ key: part.currency, value: 1 })}} ({{part.forecastedPricePercent}}%)</td>
                  <td>{{(part.signedPriceCents + part.adjustedPriceCents) | formatCurrency({ key: part.currency, value: 1 })}} ({{part.adjustedPricePercent}}%)</td>
                  <td>{{part.currentPriceCents | formatCurrency({ key: part.currency, value: 1 })}} ({{part.currentPercent}}%)</td>
                  <td>{{ part.contractor ? part.contractor.companyName : '' }}</td>
                </tr>
              </tbody>
            </v-simple-table>
            <v-divider v-if="purchaseOrderList.length"/>
            <small v-if="purchaseOrderList.length"> <b>{{ $t('contract.purchaseOrders') }}</b> </small>
            <p
              v-for="(number, index) in purchaseOrderList"
              :key="index"
              class="purchase-order"
            >
              {{number.purchaseOrder}} - {{number.name}}
            </p>
        </v-card-text>
        </v-card>
      </v-col>

    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-text>
            <v-row>
              <v-col cols="2">
                <small> <b>{{ $t('contract.signatureDate') }}</b> </small>
                <p>{{ contract.signatureDate | formatDate }}</p>
              </v-col>
              <v-col>
                <small><b> {{ $t('contract.commencementDate') }}</b> </small>
                <p>{{ contract.commencementDate | formatDate }}</p>
              </v-col>
              <v-col>
                <small> <b>{{ $t('contract.completionTime') }}</b> </small>
                <p>{{ contract.completionTime | formatDate }}</p>
              </v-col>
              <v-col>
                <small> <b>{{ $t('contract.adjustedCompletionTime') }}</b> </small>
                <InfoTooltip
                  :text="$t('contract.adjustedCompletionTimeTip')"
                />
                <p>{{ contract.adjustedCompletionTime | formatDate }} (+{{adjustedDays}})</p>
              </v-col>
              <v-col cols="2">
                <small><b>{{ $t('contract.finalTakingOver') }}</b></small>
                <p>{{ contract.takingOverDate | formatDate }}</p>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

  </v-container>
</template>

<script>
import ContractInfoMixin from '@/mixins/ContractInfoMixin'
import { getLocalizationContext } from '@/helpers/translations'

export default {
  name: 'ContractInfo',
  props: {
    contract: {
      type: Object,
      required: true
    }
  },
  mixins: [ContractInfoMixin],
  data () {
    return { }
  },
  computed: {
    partTranslationKey () {
      return getLocalizationContext(this.contract.isMultiCurrency)
    }
  }
}
</script>

<style>
.purchase-order {
  margin: 5px 0!important;
}
</style>
